// Enum to match backend error types
export enum BomBuilderErrorType {
  MULTIPLE_INSTANCES_CONFLICTING_DATA = 'multiple_instances_conflicting_data',
  MULTIPLE_INSTANCES_CONFLICTING_STRUCTURE = 'multiple_instances_conflicting_structure',
  MULTIPLE_INSTANCES_ON_SAME_LEVEL = 'multiple_instances_on_same_level',
  PART_MAP_ITEM_MISSING_REQUIRED_INFORMATION = 'part_map_item_missing_required_information',
  PURCHASED_PART_WITH_CHILDREN = 'purchased_part_with_children',
  INVALID_OR_EXPIRED_PURCHASED_COMPONENTS = 'invalid_purchased_components',
  EMPTY_CHILD_ROWS = 'empty_child_rows',
  SAME_PRIMARY_AND_SUPPORTING_FILE = 'same_primary_and_supporting_file',
  INVALID_PROMOTED_FILE = 'invalid_promoted_file',
  QUANTITY_TOO_LARGE = 'quantity_too_large',
  TOTAL_BOM_QUANTITY_TOO_LARGE = 'total_bom_quantity_too_large',
  PART_NUMBER_TOO_LONG = 'part_number_too_long',
  PART_REVISION_TOO_LONG = 'part_revision_too_long',
}

export type BomBuilderMultipleInstancesConflictingDataError = {
  errorType: BomBuilderErrorType.MULTIPLE_INSTANCES_CONFLICTING_DATA;
  fields: {
    partNumber: string;
    revision: string;
    partUuids: string[];
    conflictingFields: string[];
  };
};

export type BomBuilderMultipleInstancesConflictingStructureError = {
  errorType: BomBuilderErrorType.MULTIPLE_INSTANCES_CONFLICTING_STRUCTURE;
  fields: {
    parentUuids: string[];
    conflictingFields: string[];
  };
};

export type BomBuilderMultipleInstancesOnSameLevelError = {
  errorType: BomBuilderErrorType.MULTIPLE_INSTANCES_ON_SAME_LEVEL;
  fields: {
    parentUuid: string;
    partUuids: string[];
  };
};

export type PartMapItemMissingRequiredInformationError = {
  errorType: BomBuilderErrorType.PART_MAP_ITEM_MISSING_REQUIRED_INFORMATION;
  fields: {
    partUuid: string;
  };
};

export type PurchasedPartWithChildrenError = {
  errorType: BomBuilderErrorType.PURCHASED_PART_WITH_CHILDREN;
  fields: {
    partUuid: string;
  };
};

export type InvalidPurchasedComponentsError = {
  errorType: BomBuilderErrorType.INVALID_OR_EXPIRED_PURCHASED_COMPONENTS;
  fields: {
    partUuids: string[];
  };
};

export type EmptyChildRowsError = {
  errorType: BomBuilderErrorType.EMPTY_CHILD_ROWS;
  fields: {
    partUuids: string[];
  };
};

export type SamePrimaryAndSupportingFileError = {
  errorType: BomBuilderErrorType.SAME_PRIMARY_AND_SUPPORTING_FILE;
  fields: {
    partUuid: string;
    filename: string;
  };
};

export type InvalidPromotedFileError = {
  errorType: BomBuilderErrorType.INVALID_PROMOTED_FILE;
  fields: {
    partUuid: string;
    filename: string;
  };
};

export type QuantityTooLargeError = {
  errorType: BomBuilderErrorType.QUANTITY_TOO_LARGE;
  fields: {
    partUuid: string;
  };
};

export type TotalBomQuantityTooLargeError = {
  errorType: BomBuilderErrorType.TOTAL_BOM_QUANTITY_TOO_LARGE;
};

export type PartNumberTooLongError = {
  errorType: BomBuilderErrorType.PART_NUMBER_TOO_LONG;
  fields: { partUuid: string };
};

export type PartRevisionTooLongError = {
  errorType: BomBuilderErrorType.PART_REVISION_TOO_LONG;
  fields: { partUuid: string };
};

export type BomBuilderErrors =
  | BomBuilderMultipleInstancesConflictingDataError
  | BomBuilderMultipleInstancesConflictingStructureError
  | BomBuilderMultipleInstancesOnSameLevelError
  | PartMapItemMissingRequiredInformationError
  | PurchasedPartWithChildrenError
  | InvalidPurchasedComponentsError
  | EmptyChildRowsError
  | SamePrimaryAndSupportingFileError
  | InvalidPromotedFileError
  | QuantityTooLargeError
  | TotalBomQuantityTooLargeError
  | PartNumberTooLongError
  | PartRevisionTooLongError;

// Determines if this is a BOM Builder error that should be formatted for the user
export const isBomBuilderFetchError = (
  extra: Record<string, unknown>,
  message: string,
): extra is BomBuilderErrors => {
  return Object.values(BomBuilderErrorType).includes(
    message as BomBuilderErrorType,
  );
};
